import React from "react";
import { CircularProgress } from "material-ui";
import { Pagination } from "react-bootstrap";
import { connect } from "react-redux";
import Header from "../../header";
import RedirectIfNotLogged from "../../menu/redirect-if-not-logged";
import * as SearchService from "../../../services/search-service";
import * as ErrorAction from "../../../redux/action/error-action";
import * as PaperStates from "../../../general/paper-state";
import { highlight } from "../../../general/search-highlight";
import { DS } from "../../../theme-default";

const PAGE_SIZE = 20;

const stateColor = (state) => {
  if (PaperStates.isApproved(state)) return DS.success;
  if (PaperStates.isRejected(state)) return DS.error;
  if (PaperStates.isDraft(state)) return DS.textMuted;
  return DS.warning;
};

const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  const number = Number(value);
  if (isNaN(number)) return "";
  return number.toLocaleString("ro-RO", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "";
  return (
    date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear()
  );
};

const cellStyle = {
  padding: "10px 12px",
  borderBottom: "1px solid " + DS.borderFaint,
  fontSize: "15px",
  color: DS.ink,
  verticalAlign: "top",
  textAlign: "left",
};

const headCellStyle = {
  ...cellStyle,
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.5,
  color: DS.textSecondary,
  borderBottom: "1px solid " + DS.border,
};

class SearchResultsPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      results: null,
    };
    this.requestSeq = 0;
  }

  componentDidMount() {
    this.props.hideError();
    const query = this._queryFromUrl();
    if (query) {
      this._fetch(query, 0, 0);
    }
  }

  componentDidUpdate(prevProps) {
    // The global search bar drives this page: a new query arrives as a URL
    // change (it pushes /app/search?q=...), never through local input.
    if (prevProps.location.search !== this.props.location.search) {
      const query = this._queryFromUrl();
      if (query) {
        this._fetch(query, 0, 0);
      } else {
        this.requestSeq++; // invalidate any in-flight request
        this.setState({ results: null, loading: false });
      }
    }
  }

  _queryFromUrl = () => {
    const params = new URLSearchParams(this.props.location.search);
    return (params.get("q") || "").trim();
  };

  _fetch = (query, papersPage, contractsPage) => {
    const seq = ++this.requestSeq;
    this.setState({ loading: true });
    SearchService.globalSearch(query, PAGE_SIZE, papersPage, contractsPage)
      .then((response) => {
        if (seq !== this.requestSeq) {
          return; // stale response, a newer request is in flight
        }
        this.setState({ results: response.data, loading: false });
      })
      .catch(() => {
        if (seq !== this.requestSeq) {
          return;
        }
        this.props.showError("A aparut o eroare. Va rog reincercati!");
        this.setState({ loading: false });
      });
  };

  _openPaper = (paper) => {
    this.props.history.push(
      "/app/referate/0?filter=all&q=" + encodeURIComponent(paper.code),
    );
  };

  _openContract = (contract) => {
    const year = contract.contractDate
      ? new Date(contract.contractDate).getFullYear()
      : new Date().getFullYear();
    this.props.history.push(
      "/app/contracts?year=" +
        year +
        "&q=" +
        encodeURIComponent(contract.contractNr || ""),
    );
  };

  _onPapersPageChanged = (newPage) => {
    const results = this.state.results;
    const contractsPage =
      results && results.contracts ? results.contracts.page : 0;
    this._fetch(this._queryFromUrl(), parseInt(newPage, 10) - 1, contractsPage);
  };

  _onContractsPageChanged = (newPage) => {
    const results = this.state.results;
    const papersPage = results && results.papers ? results.papers.page : 0;
    this._fetch(this._queryFromUrl(), papersPage, parseInt(newPage, 10) - 1);
  };

  _groupTitle = (label, total) => (
    <h3
      style={{
        fontSize: "1.05rem",
        fontWeight: 600,
        color: DS.ink,
        margin: "28px 0 10px 0",
      }}
    >
      {label}
      <span style={{ color: DS.textMuted, fontWeight: 400, marginLeft: 8 }}>
        {total}
      </span>
    </h3>
  );

  _renderPapers = () => {
    const { results } = this.state;
    const query = this._queryFromUrl();
    if (!results || !results.papers) {
      return null;
    }
    const group = results.papers;
    return (
      <div>
        {this._groupTitle("Referate", group.total)}
        {group.items.length === 0 ? (
          <div style={{ color: DS.textSecondary }}>Niciun referat gasit.</div>
        ) : (
          <div
            style={{
              border: "1px solid " + DS.border,
              borderRadius: DS.radiusCard,
              backgroundColor: DS.surface,
              overflowX: "auto",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={headCellStyle}>Cod</th>
                  <th style={headCellStyle}>Continut</th>
                  <th style={headCellStyle}>Compartiment</th>
                  <th style={headCellStyle}>Categorie</th>
                  <th style={headCellStyle}>Sursa finantare</th>
                  <th style={headCellStyle}>Creat de</th>
                  <th style={headCellStyle}>Data</th>
                  <th style={headCellStyle}>Valoare (cu TVA)</th>
                  <th style={headCellStyle}>Stare</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((paper) => (
                  <tr
                    key={paper.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => this._openPaper(paper)}
                  >
                    <td style={{ ...cellStyle, whiteSpace: "nowrap", fontWeight: 600 }}>
                      {highlight(paper.code, query)}
                    </td>
                    <td style={cellStyle}>{highlight(paper.snippet, query)}</td>
                    <td style={cellStyle}>
                      {highlight(paper.department, query)}
                    </td>
                    <td style={cellStyle}>
                      {highlight(paper.acquisitionCategory, query)}
                    </td>
                    <td style={cellStyle}>
                      {highlight(paper.financeSource, query)}
                    </td>
                    <td style={cellStyle}>
                      {highlight(paper.createdBy, query)}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      {formatDate(paper.createdDate)}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        whiteSpace: "nowrap",
                        textAlign: "right",
                      }}
                    >
                      {formatValue(paper.totalPriceWithVat)}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        color: stateColor(paper.state),
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {PaperStates.getTypeForId(paper.state)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {group.totalPages > 1 && (
          <Pagination
            prev
            next
            ellipsis
            boundaryLinks
            maxButtons={10}
            items={group.totalPages}
            activePage={group.page + 1}
            onSelect={this._onPapersPageChanged}
          />
        )}
      </div>
    );
  };

  _renderContracts = () => {
    const { results } = this.state;
    const query = this._queryFromUrl();
    if (!results || results.contracts === null || results.contracts === undefined) {
      return null; // caller cannot see contracts, the group is absent
    }
    const group = results.contracts;
    return (
      <div>
        {this._groupTitle("Contracte", group.total)}
        {group.items.length === 0 ? (
          <div style={{ color: DS.textSecondary }}>Niciun contract gasit.</div>
        ) : (
          <div
            style={{
              border: "1px solid " + DS.border,
              borderRadius: DS.radiusCard,
              backgroundColor: DS.surface,
              overflowX: "auto",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={headCellStyle}>Nr.</th>
                  <th style={headCellStyle}>Data</th>
                  <th style={headCellStyle}>Obiect</th>
                  <th style={headCellStyle}>Furnizor</th>
                  <th style={headCellStyle}>Valoare</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((contract) => (
                  <tr
                    key={contract.id}
                    style={{ cursor: "pointer" }}
                    onClick={() => this._openContract(contract)}
                  >
                    <td style={{ ...cellStyle, whiteSpace: "nowrap", fontWeight: 600 }}>
                      {highlight(contract.contractNr, query)}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      {formatDate(contract.contractDate)}
                    </td>
                    <td style={cellStyle}>
                      {highlight(
                        contract.contractObject || contract.serviceName,
                        query,
                      )}
                    </td>
                    <td style={cellStyle}>
                      {highlight(contract.supplier, query)}
                    </td>
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      {contract.contractValue}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {group.totalPages > 1 && (
          <Pagination
            prev
            next
            ellipsis
            boundaryLinks
            maxButtons={10}
            items={group.totalPages}
            activePage={group.page + 1}
            onSelect={this._onContractsPageChanged}
          />
        )}
      </div>
    );
  };

  render() {
    const { loading, results } = this.state;
    const query = this._queryFromUrl();
    const totalCount = results
      ? (results.papers ? results.papers.total : 0) +
        (results.contracts ? results.contracts.total : 0)
      : 0;

    return (
      <div>
        <RedirectIfNotLogged />
        <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
          <Header title={"Cautare documente"} />
          {query && (
            <span style={{ color: DS.textSecondary, fontSize: "1rem" }}>
              rezultate pentru „{query}”
            </span>
          )}
        </div>

        {loading && (
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <CircularProgress />
          </div>
        )}

        {!loading && !query && (
          <div style={{ color: DS.textSecondary, marginTop: 30 }}>
            Folositi campul de cautare din bara de sus (Ctrl+K).
          </div>
        )}

        {!loading && query && results && totalCount === 0 && (
          <div style={{ textAlign: "center", marginTop: 50 }}>
            <h3>Niciun rezultat pentru „{query}”.</h3>
          </div>
        )}

        {!loading && results && totalCount > 0 && (
          <div style={{ maxWidth: 1320 }}>
            {this._renderPapers()}
            {this._renderContracts()}
          </div>
        )}
      </div>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return {
    showError: (text) => dispatch(ErrorAction.showError(text)),
    hideError: () => dispatch(ErrorAction.hideError()),
  };
}

export default connect(null, mapDispatchToProps)(SearchResultsPage);
